import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import React, {
  cloneElement,
  Context,
  createContext,
  createRef,
  MouseEvent,
  PureComponent,
  ReactElement,
  ReactNode
} from 'react';
import { Modal, ModalClose } from '../../components/Modal';
import { Overlay } from '../../components/Overlay';
import { SvgIcon } from '../../components/SvgIcon';
import { withErrorBoundary } from '../../error-handling/withErrorBoundary';
import { Maybe } from '../../types';
import { selectIsStaticContentLoaded } from '../Typo3Provider/selectors';
import { withTypo3, WithTypo3Props } from '../Typo3Provider/withTypo3';

type ModalElement<Props = {}> = ReactElement<
  Props & {
    closeModal?: () => void;
  }
>;

export interface ModalOptions {
  plain?: boolean;
  gallery?: boolean;
  centered?: boolean;
  zoom?: boolean;
  forVideo?: boolean;
}

interface ModalInstance {
  element: ModalElement<any>;
  options: Maybe<ModalOptions>;
  callback: Maybe<() => void>;
  key: string;
}

interface ModalProviderProps {
  children: ReactNode;
}

interface ModalProviderState {
  instances: ModalInstance[];
  currentInstance: Maybe<ModalInstance>;
}

export type ShowModal = (
  element: ModalElement<any>,
  options?: ModalOptions,
  callback?: () => void
) => void;

export interface ModalContextApi {
  showModal: ShowModal;
  dismissCurrentModal: () => void;
}

type ModalContextType = Context<ModalContextApi>;

const defaultContextValue: ModalContextApi = {
  showModal: () => {},
  dismissCurrentModal: () => {}
};

export const ModalContext: ModalContextType = createContext(
  defaultContextValue
);

class ModalProvider extends PureComponent<
  ModalProviderProps & WithTypo3Props,
  ModalProviderState
> {
  public modalIndex: number = 0;

  public overlayRef = createRef<HTMLDivElement>();

  public state: ModalProviderState = {
    instances: [],
    currentInstance: null
  };

  public componentDidUpdate() {
    const { currentInstance } = this.state;
    const { current: overlayRef } = this.overlayRef;
    if (currentInstance) {
      overlayRef &&
        disableBodyScroll(overlayRef, {
          reserveScrollBarGap: true
        });
    } else {
      // clearAllBodyScrollLocks(overlayRef);
      clearAllBodyScrollLocks();
    }
  }

  public appendModal(instance: ModalInstance) {
    if (!this.state.currentInstance) {
      // present Modal
      this.setState({
        currentInstance: instance
      });
    } else {
      // cache Modal
      this.setState(({ instances }) => ({
        instances: [...instances, instance]
      }));
    }
  }

  public showModal = (
    element: ModalElement<any>,
    options: Maybe<ModalOptions> = null,
    callback?: () => void
  ) => {
    document.querySelectorAll('video').forEach(vid => vid.pause());
    this.appendModal({
      element,
      options,
      callback,
      key: `ModalInstance_${this.modalIndex++}`
    });
  };

  public dismissCurrentModal = () => {
    const { currentInstance } = this.state;
    if (currentInstance) {
      const { callback } = currentInstance;
      this.setState(
        ({ instances }) => ({
          currentInstance: instances[0],
          instances: instances.slice(1)
        }),
        () => {
          callback && callback();
        }
      );
    }
  };

  public closeModal = () => {
    this.dismissCurrentModal();
  };

  public preventCloseModal = (e: MouseEvent<any>) => {
    e.stopPropagation();
  };

  public render() {
    const { children, typo3 } = this.props;
    const { currentInstance } = this.state;
    return (
      <ModalContext.Provider value={this}>
        {children}
        {selectIsStaticContentLoaded(typo3) && currentInstance && (
          <Overlay
            style={{
              overflow: 'auto'
            }}
            onClick={this.closeModal}
            key={currentInstance.key}
            innerRef={this.overlayRef}
          >
            <Modal
              onClick={this.preventCloseModal}
              {...currentInstance.options}
            >
              <ModalClose onClick={this.closeModal}>
                <SvgIcon icon={'close'} />
              </ModalClose>
              {cloneElement(currentInstance.element, {
                closeModal: this.closeModal
              })}
            </Modal>
          </Overlay>
        )}
      </ModalContext.Provider>
    );
  }
}

export default withErrorBoundary(withTypo3(ModalProvider), 'ModalProvider');
